import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCurrentUser } from '@/hooks';
import { Alert, Button, Snackbar, Typography } from '@/ui';
import { track } from '@/utils';

import { useUpdateUserAssessmentMutation } from '@/store/api/userApi';
import { captureException } from '@/utils/errorMonitoring';

const OnboardingComplete = () => {
  const navigate = useNavigate();
  const { user, refetchUser } = useCurrentUser();
  const [updateUserAssessment] = useUpdateUserAssessmentMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const completingRef = useRef(false);

  useEffect(() => {
    track('ob_complete');
  }, []);

  const ensureAssessmentComplete = useCallback(
    async (surfaceError = false) => {
      if (!user.id) return;
      if (user.userAssesment?.complete) return;
      if (completingRef.current) return;

      completingRef.current = true;
      try {
        await updateUserAssessment({
          id: user.id,
          userAssesment: { complete: true },
        }).unwrap();
        await refetchUser();
      } catch (error) {
        captureException(error);
        if (surfaceError) {
          setErrorMessage(
            'We couldn’t mark onboarding as complete. You may be redirected back to Get Started until this saves.',
          );
        }
      } finally {
        // eslint-disable-next-line require-atomic-updates -- ref updates are intentional; we only use this as an in-flight guard
        completingRef.current = false;
      }
    },
    [refetchUser, updateUserAssessment, user.id, user.userAssesment?.complete],
  );

  useEffect(() => {
    if (!user.id) return;
    ensureAssessmentComplete(false).catch(() => undefined);
  }, [ensureAssessmentComplete, user.id]);

  return (
    <main className="bg-gradient-dark-sunny flex flex-1 flex-col overflow-auto">
      <div className="container mx-auto flex flex-1 flex-col items-center justify-center p-4">
        <div className="max-w-xl text-center">
          <Typography className="mb-6" variant="h4">
            You're all set!
          </Typography>
          <Typography className="mb-4" variant="body1">
            We look forward to matching you with wonderful coaches.
          </Typography>
          <Typography className="mb-8" variant="body1">
            If you have any questions, please reach out to{' '}
            <a
              className="text-primary hover:underline"
              href="mailto:concierge@mento.co"
            >
              concierge@mento.co
            </a>
          </Typography>
          <Button
            size="large"
            variant="contained"
            onClick={async () => {
              setErrorMessage(null);
              await ensureAssessmentComplete(true);
              navigate('/m');
            }}
          >
            Go to your Mento account
          </Button>
        </div>
      </div>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        autoHideDuration={7000}
        open={Boolean(errorMessage)}
        onClose={() => setErrorMessage(null)}
      >
        <Alert
          severity="error"
          style={{ width: '100%' }}
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </main>
  );
};

export default OnboardingComplete;
